// /events/:eventId/chat — the live event thread.
//
// The socket is subscribed on mount and released on unmount. The router clears
// the outlet without notifying anyone, so a view that forgets to release leaks a
// handler rendering into detached nodes forever — hence the explicit teardown
// registered with `onLeave`.
//
// Every member of the group can read and post (docs/plan-event-conversation.md);
// only NOTIFICATIONS depend on the RSVP. A 403 therefore means "not in this
// group". Per person there is a Mute, organizers can Close or Reopen and flag a
// message Important, and any message can be shared to the whole group.

import {
  ApiClientError,
  deleteChatMessage,
  fetchChat,
  fetchEvent,
  markChatRead,
  postChatMessage,
  putChatReaction,
  removeChatReaction,
  setChatClosed,
  setChatMute,
  shareChatMessage,
} from '../api/client.js';
import { navigate, onLeave, type View } from '../router.js';
import { el } from '../dom.js';
import {
  eventChannelName,
  fmtInstant,
  TimeFormat,
  type ChatMessage,
  type ChatPage,
  type EventDetail,
  type ReactionRoster,
} from '@msc/shared';
import { subscribe } from '../realtime.js';
import { viewerZone } from '../viewerZone.js';
import { blockButton, reportButton } from './reportControl.js';
import { reactionBar, type ReactionApi } from './reactionBar.js';
import { getCurrentUserId } from '../chrome.js';

export const chatView: View = async (outlet, ctx) => {
  const eventId = Number(ctx.params.eventId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(eventId) || eventId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid event.'));
    return;
  }

  let page: ChatPage;
  let event: EventDetail;
  let zone: string;
  // The viewer's own id, so a message's public roster can say which reaction is
  // theirs. `mine` on the message is about AUTHORSHIP and cannot answer this.
  let viewerId: number | null;
  try {
    [event, page, zone, viewerId] = await Promise.all([
      fetchEvent(eventId),
      fetchChat(eventId),
      viewerZone(),
      getCurrentUserId(),
    ]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    const forbidden = err instanceof ApiClientError && err.status === 403;
    main.replaceChildren(
      el('h1', {}, forbidden ? 'Members only' : 'Could not open the conversation'),
      el(
        'p',
        { class: forbidden ? 'muted' : 'form-error' },
        forbidden
          ? 'This conversation is limited to members of the group.'
          : 'Something went wrong loading the messages.',
      ),
      el('p', {}, [el('a', { href: `/events/${eventId}` }, 'Back to the event')]),
    );
    return;
  }

  // Oldest at the top, newest at the bottom, scrolled to the bottom — a chat.
  const list = el('div', { class: 'chat-list' });
  const olderBtn = el('button', { class: 'btn btn-link btn-standalone', type: 'button' }, 'Load earlier messages');
  const olderWrap = el('div', {}, page.hasMore ? [olderBtn] : []);

  /** Message ids already rendered, so a broadcast echo cannot duplicate a row. */
  const seen = new Set<number>();

  function append(message: ChatMessage, atTop = false): void {
    if (seen.has(message.messageId)) return;
    seen.add(message.messageId);
    const node = messageRow(message, zone, viewerId, () => node.remove(), true);
    if (atTop) list.prepend(node);
    else list.append(node);
  }

  /**
   * Apply a live reaction update to a message already on screen.
   *
   * Rebuilds only the bar, never the row: replacing the whole message would
   * scrap the delete and report buttons mid-thread and, on a long thread, shift
   * the scroll under someone's reading position for a reaction they did not
   * make. A message NOT on screen is simply skipped — it either has not loaded
   * yet or was paged past, and either way it will carry the roster when it does
   * arrive, because the roster is fetched with the page.
   */
  function applyReactionUpdate(messageId: number, reactions: ReactionRoster): void {
    const row = list.querySelector(`[data-message="${messageId}"]`);
    const bar = row?.querySelector('.reaction-bar');
    if (!bar) return;
    const target = { reactions };
    bar.replaceWith(reactionBar(target, viewerId, chatReactionApi(messageId)));
  }

  function scrollToBottom(): void {
    list.scrollTop = list.scrollHeight;
  }

  for (const m of page.messages) append(m);

  let oldest = page.oldestId;
  olderBtn.addEventListener('click', async () => {
    if (oldest === null) return;
    olderBtn.setAttribute('disabled', 'disabled');
    try {
      const older = await fetchChat(eventId, oldest);
      // Prepended in reverse so the batch keeps its order once each lands at the
      // top. Prepending them forwards would reverse the batch.
      for (const m of [...older.messages].reverse()) append(m, true);
      oldest = older.oldestId;
      if (!older.hasMore) olderWrap.replaceChildren();
    } catch {
      olderBtn.textContent = 'Could not load earlier messages';
    } finally {
      olderBtn.removeAttribute('disabled');
    }
  });

  // --- Live updates -------------------------------------------------------
  //
  // Subscribed here and released via onLeave. The socket itself is a session-long
  // singleton (see realtime.ts) and survives navigating away and back.
  const release = subscribe(eventChannelName(eventId), (frame) => {
    if (frame.event === 'chat.message') {
      const atBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 80;
      append(frame.data);
      // Only auto-scroll if they were already at the bottom. Yanking the view
      // while someone reads back through history is the classic chat annoyance.
      if (atBottom) scrollToBottom();
      // Receiving a message while the thread is open counts as reading it.
      void markChatRead(eventId).catch(() => undefined);
    } else if (frame.event === 'chat.deleted') {
      list.querySelector(`[data-message="${frame.data.messageId}"]`)?.remove();
      seen.delete(frame.data.messageId);
    } else if (frame.event === 'chat.reaction') {
      applyReactionUpdate(frame.data.messageId, frame.data.reactions);
    }
  });
  onLeave(release);

  // --- Mute and close ----------------------------------------------------
  let muted = page.muted;
  let closed = page.closed;
  const stateNote = el('p', { class: 'muted small', role: 'status' });
  const muteBtn = el('button', { class: 'btn btn-link', type: 'button' });
  const closeBtn = el('button', { class: 'btn btn-link', type: 'button' });
  const composerSlot = el('div', {});

  function paintControls(): void {
    muteBtn.textContent = muted ? 'Unmute this conversation' : 'Mute this conversation';
    closeBtn.textContent = closed ? 'Reopen this conversation' : 'Close this conversation';
    composerSlot.replaceChildren(
      closed
        ? el('p', { class: 'muted' }, 'This conversation is closed. You can still read it.')
        : composer(eventId, page.canOrganize, (m) => {
            append(m);
            scrollToBottom();
          }),
    );
  }

  muteBtn.addEventListener('click', async () => {
    muteBtn.setAttribute('disabled', 'disabled');
    try {
      muted = (await setChatMute(eventId, !muted)).muted;
      stateNote.textContent = muted
        ? 'Muted. No notifications from this conversation, except messages an organizer marks important.'
        : 'Unmuted.';
    } catch {
      stateNote.textContent = 'Could not save that. Try again.';
    } finally {
      muteBtn.removeAttribute('disabled');
      paintControls();
    }
  });
  closeBtn.addEventListener('click', async () => {
    closeBtn.setAttribute('disabled', 'disabled');
    try {
      closed = (await setChatClosed(eventId, !closed)).closed;
      stateNote.textContent = closed ? 'Closed.' : 'Reopened.';
    } catch {
      stateNote.textContent = 'Could not save that. Try again.';
    } finally {
      closeBtn.removeAttribute('disabled');
      paintControls();
    }
  });
  paintControls();

  main.replaceChildren(
    el('p', {}, [el('a', { href: `/events/${eventId}` }, `Back to ${event.title}`)]),
    el('div', { class: 'page-head' }, [el('h1', {}, `${event.title} conversation`)]),
    el(
      'p',
      { class: 'muted small' },
      'Everyone in the group can read this. People who said No, or muted it, are not notified.',
    ),
    el('p', { class: 'small' }, page.canOrganize ? [muteBtn, ' ', closeBtn] : [muteBtn]),
    stateNote,
    olderWrap,
    list,
    composerSlot,
  );

  scrollToBottom();
  // Opening the thread marks it read.
  void markChatRead(eventId).catch(() => undefined);
};

/** The chat thread's endpoints, for the shared reaction bar. */
function chatReactionApi(messageId: number): ReactionApi {
  return {
    put: async (kind) => (await putChatReaction(messageId, kind)).reactions,
    remove: async () => (await removeChatReaction(messageId)).reactions,
  };
}

export function messageRow(
  message: ChatMessage,
  zone: string,
  viewerId: number | null,
  onRemoved: () => void,
  /** The thread view offers Share to group; the home stream does not. */
  canShare = false,
): HTMLElement {
  const meta = el('p', { class: 'chat-meta' }, [
    el('strong', {}, message.displayName),
    ' ',
    el('span', { class: 'muted' }, fmtInstant(message.createdAt, zone, TimeFormat.INSTANT)),
    ...(message.important ? [' ', el('strong', { class: 'danger' }, 'Important')] : []),
  ]);

  if (canShare && message.body) {
    const share = el('button', { class: 'btn btn-link', type: 'button' }, 'Share to group');
    share.addEventListener('click', async () => {
      if (!window.confirm('Post this message to the whole group feed?')) return;
      share.setAttribute('disabled', 'disabled');
      try {
        await shareChatMessage(message.messageId);
        share.textContent = 'Shared';
      } catch (err) {
        window.alert(err instanceof ApiClientError ? err.message : 'Could not share that.');
        share.removeAttribute('disabled');
      }
    });
    meta.append(' ', share);
  }

  if (message.canDelete) {
    const btn = el('button', { class: 'btn btn-link btn-danger', type: 'button' }, 'Delete');
    btn.addEventListener('click', async () => {
      if (!window.confirm('Delete this message?')) return;
      btn.setAttribute('disabled', 'disabled');
      try {
        await deleteChatMessage(message.messageId);
        // The broadcast will also remove it; removing here too means the author
        // sees it go immediately even if their own socket is briefly down.
        onRemoved();
      } catch (err) {
        window.alert(err instanceof ApiClientError ? err.message : 'Could not delete that.');
        btn.removeAttribute('disabled');
      }
    });
    meta.append(' ', btn);
  }

  // Guideline 1.2, same control as the feed and the photo grid.
  const report = reportButton('message', message.messageId, message.mine);
  if (report) meta.append(' ', report);
  const block = blockButton(message.userId, message.displayName, message.mine);
  if (block) meta.append(' ', block);

  const children: HTMLElement[] = [meta];
  if (message.body) children.push(el('p', { class: 'chat-body' }, message.body));
  if (message.photoUrl) {
    children.push(
      el('img', { class: 'chat-photo', src: message.photoUrl, alt: '', loading: 'lazy' }),
    );
  }

  // Same position as on a feed post: last, under the content it is about.
  children.push(reactionBar(message, viewerId, chatReactionApi(message.messageId)));

  return el(
    'article',
    {
      class: `chat-msg${message.mine ? ' chat-mine' : ''}`,
      'data-message': message.messageId,
      'data-author': message.userId,
    },
    children,
  );
}

/**
 * The composer.
 *
 * No optimistic insert: the broadcast arrives in milliseconds on a working
 * socket, and an optimistic row would need reconciling against it. The input is
 * cleared immediately, which is the part that actually feels fast.
 */
function composer(
  eventId: number,
  canOrganize: boolean,
  onPosted: (m: ChatMessage) => void,
): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const input = el('textarea', {
    id: 'chat-body',
    rows: 2,
    placeholder: 'Say something',
    maxlength: 4000,
  }) as HTMLTextAreaElement;
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Send');
  // Organizers only: an Important message rings through a mute.
  const important = el('input', { type: 'checkbox' }) as HTMLInputElement;
  const importantRow = canOrganize
    ? el('label', { class: 'check' }, [important, ' Mark important (notifies everyone, even if muted)'])
    : null;

  const form = el('form', { method: 'post', action: `/events/${eventId}/chat`, novalidate: true }, [
    input,
    ...(importantRow ? [importantRow] : []),
    error,
    submit,
  ]) as HTMLFormElement;

  async function send(): Promise<void> {
    const body = input.value.trim();
    if (body === '') return;

    error.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    // Cleared before the request: the message is going, and a field that stays
    // full for 200ms invites a double send.
    input.value = '';
    try {
      onPosted(await postChatMessage(eventId, body, canOrganize && important.checked));
      important.checked = false;
    } catch (err) {
      // Put the text back so nothing is lost.
      input.value = body;
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Could not send that message.';
      error.hidden = false;
    } finally {
      submit.removeAttribute('disabled');
      input.focus();
    }
  }

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    void send();
  });

  // Enter sends, Shift+Enter makes a newline — the convention everywhere else.
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      void send();
    }
  });

  return el('section', { class: 'card chat-composer' }, [form]);
}
